import { useCallback, useRef, useState } from 'react';

import type { RuntimeMode } from '../api/types';

export interface UserAttachment {
  filename: string;
  path: string;
}

export interface UserTimelineItem {
  kind: 'user';
  id: string;
  text: string;
  attachments?: UserAttachment[];
}

export interface AgentTimelineItem {
  kind: 'agent';
  id: string;
  text: string;
}

export interface CodeTimelineItem {
  kind: 'code';
  id: string;
  execId: string;
  code: string;
  output: string;
  outputType: string;
  metadata: Record<string, unknown>;
  status: 'running' | 'success' | 'error';
}

export interface SystemTimelineItem {
  kind: 'system';
  id: string;
  text: string;
}

export interface DownloadTimelineItem {
  kind: 'download';
  id: string;
  downloadId: string;
  filename: string;
  size: number;
  label?: string;
}

export interface SubagentConversationTurn {
  role: 'user' | 'agent' | 'system';
  text: string;
  runId: string;
}

export interface SubagentTimelineItem {
  kind: 'subagent';
  id: string;
  agentId: number;
  parentId: number | null;
  task: string;
  status: 'running' | 'done' | 'error';
  workingStatus: string;
  resultSummary: string;
  conversation: string;
  conversationTurns: SubagentConversationTurn[];
  lastRunId: string;
}

export interface AuthRequestTimelineItem {
  kind: 'auth_request';
  id: string;
  provider: string;
  displayName: string;
  iconUrl: string;
  authStartUrl: string;
  status: 'pending' | 'success' | 'error';
}

export interface ConsoleTimelineItem {
  kind: 'console';
  id: string;
  sections: string[];
}

export type TimelineItem =
  | UserTimelineItem
  | AgentTimelineItem
  | CodeTimelineItem
  | SystemTimelineItem
  | DownloadTimelineItem
  | SubagentTimelineItem
  | AuthRequestTimelineItem
  | ConsoleTimelineItem;

export interface RuntimeInfo {
  runtimeId: string;
  mode: RuntimeMode;
  startedAt: string;
}

export type ChatError =
  | { type: 'payment-required' }
  | { type: 'unknown'; message: string };

export interface ChatState {
  sessionId: string;
  timeline: TimelineItem[];
  isRunning: boolean;
  error: ChatError | null;
  runtimeState: RuntimeInfo | null;
  model: string | null;

  /**
   * Latest phrase emitted by the agent's `report_partial_status` tool.
   * Empty string means "show the default Working… label". Overwritten
   * by each `working_status` event; cleared when a run ends.
   */
  workingStatus: string;

  /**
   * True iff the most recent run event was an agent `text_delta` that
   * appended visible content to the timeline — i.e. the agent bubble is
   * actively growing on screen. Used by the `WorkingBubble` to decide
   * whether its "Working…" indicator would be redundant with the
   * streaming text. Any non-text event resets this to false, so the
   * bubble reappears during code-block generation and execution even
   * when the previous agent text is still the last timeline item.
   */
  isStreamingAgentText: boolean;

  seq: number;
  runId: string | null;
  agentMessageId: string | null;
  runningCodeExecIds: Set<string>;
  bufferedAgentTextDelta: string;
}

export function createChatState(
  sessionId: string,
  opts?: { timeline?: TimelineItem[]; model?: string | null },
): ChatState {
  return {
    sessionId,
    timeline: opts?.timeline ?? [],
    isRunning: false,
    error: null,
    runtimeState: null,
    model: opts?.model ?? null,
    workingStatus: '',
    isStreamingAgentText: false,
    seq: -1,
    runId: null,
    agentMessageId: null,
    runningCodeExecIds: new Set(),
    bufferedAgentTextDelta: '',
  };
}

export interface ChatStoreOpts {
  timeline?: TimelineItem[];
  model?: string | null;
}

export function useChatStore() {
  const chatsRef = useRef<Map<string, ChatState>>(new Map());

  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const getChat = useCallback(
    (sessionId: string): ChatState | undefined => chatsRef.current.get(sessionId),
    [],
  );

  const getOrCreateChat = useCallback(
    (sessionId: string, opts?: ChatStoreOpts): ChatState => {
      let chat = chatsRef.current.get(sessionId);
      if (!chat) {
        chat = createChatState(sessionId, opts);
        chatsRef.current.set(sessionId, chat);
      }
      return chat;
    },
    [],
  );

  const hydrateChat = useCallback(
    (sessionId: string, opts?: ChatStoreOpts) => {
      const chat = getOrCreateChat(sessionId, opts);
      if (opts?.timeline) chat.timeline = opts.timeline;
      if (opts?.model !== undefined) chat.model = opts.model;
      bump();
    },
    [getOrCreateChat, bump],
  );

  const removeChat = useCallback(
    (sessionId: string) => {
      chatsRef.current.delete(sessionId);
      bump();
    },
    [bump],
  );

  return {
    getChat,
    getOrCreateChat,
    hydrateChat,
    removeChat,
    bump,
  };
}
